import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { generatedDocumentsChannelName } from '@/channels/utils/channelNames';
import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import {
  useDocumentTypes,
  useDocumentTypeOptions,
  useGeneratedDocumentsList,
  useGenerateDocument,
  usePreviewDocument
} from '@/hooks/useGeneratedDocuments';
import PersonContext from '@/pages/Person/PersonContext';
import { GenerateDocumentRequest } from '@/types/generatedDocument';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import DualTime from '@/ui/components/DualTime/DualTime';
import Select from '@/ui/components/Form/Select';
import Heading from '@/ui/components/Heading';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import { GENERATE_DOCUMENT, hasPermission } from '@/utils/userPermission';

import { renderField } from './utils';

interface FormValues {
  [key: string]: boolean | string;
}

const PersonGeneratedDocuments = () => {
  const { person } = useContext(PersonContext);
  const { permissions, addMessage } = useContext(AppContext);
  const personId = person.id;

  const canGenerateDocument = hasPermission({ permissions, requiredPermission: GENERATE_DOCUMENT });

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [optionsErrors, setOptionsErrors] = useState<{ [key: string]: string[] }>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const { data: typesResponse, isLoading: isLoadingTypes } = useDocumentTypes();
  const { data: optionsResponse, isLoading: isLoadingOptions } = useDocumentTypeOptions(
    selectedType,
    personId
  );
  const { data: listResponse, isLoading: isLoadingList } = useGeneratedDocumentsList(
    personId,
    page
  );

  const generateMutation = useGenerateDocument();
  const previewMutation = usePreviewDocument();
  const queryClient = useQueryClient();

  useChannelData({
    channelId: generatedDocumentsChannelName(personId),
    channelName: 'Generated Documents',
    messageCallback: (message) => {
      if (message.name === 'document_ready') {
        queryClient.invalidateQueries({ queryKey: ['generatedDocuments', personId] });
        addMessage('success', 'Document ready!');
      }
    }
  });

  const { control, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (selectedType) {
      reset();
    }
  }, [selectedType, reset]);

  const onSubmit = (data: FormValues) => {
    if (!personId || !selectedType) return;

    const requestData: GenerateDocumentRequest = {
      person_id: personId,
      document_type: selectedType,
      options: data
    };

    generateMutation.mutate(requestData, {
      onSuccess: () => {
        reset();
        setSelectedType(null);
        setOptionsErrors({});
        setFormErrors([]);
        addMessage('success', 'Document generation requested successfully');
      },
      onError: (error) => {
        const options = (error as AxiosError<{ options: { [key: string]: string[] } }>).response
          ?.data?.options;
        setOptionsErrors(options || {});
        setFormErrors(
          (options && options['non_field_errors']) || [
            'An error occurred while generating the document.'
          ]
        );
      }
    });
  };

  const onPreview = (data: FormValues) => {
    if (!personId || !selectedType) return;

    const requestData: GenerateDocumentRequest = {
      person_id: personId,
      document_type: selectedType,
      options: data
    };

    previewMutation.mutate(requestData, {
      onSuccess: (response) => {
        setOptionsErrors({});
        setFormErrors([]);
        if (response.data) {
          const newWindow = window.open();
          if (newWindow) {
            newWindow.document.writeln('<p>Document Preview</p>');
            newWindow.document.writeln(response.data);
            newWindow.document.title = 'Document Preview';
            newWindow.document.close();
          }
        }
      },
      onError: (error) => {
        const options = (error as AxiosError<{ options: { [key: string]: string[] } }>).response
          ?.data?.options;
        setOptionsErrors(options || {});
        setFormErrors(
          (options && options['non_field_errors']) || [
            'An error occurred while generating the preview.'
          ]
        );
      }
    });
  };

  const renderedOptions = useMemo(() => {
    const sorted = optionsResponse?.sort((a, b) => b.order - a.order);
    return sorted?.sort((i) => -i.order).map((field) => renderField(field, control, optionsErrors));
  }, [optionsResponse, control, optionsErrors]);

  if (isLoadingTypes)
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );

  if (!canGenerateDocument) {
    return <Alert level="error">You do not have permission to generate documents.</Alert>;
  }

  return (
    <>
      <Card className="mb-6 p-2">
        <Heading value={3} className="mb-4">
          Generate New Document
        </Heading>

        <div className="mb-4">
          <Select
            label="Document Type"
            options={typesResponse ?? []}
            selectedOption={
              typesResponse?.find((type) => type.value === selectedType) || emptySelectOption
            }
            onChange={(option) => setSelectedType(option.value as string)}
            placeholder="Select a type..."
          />
        </div>

        {isLoadingOptions && (
          <div className="mt-8 flex justify-center">
            <Spinner />
          </div>
        )}

        {selectedType && Array.isArray(optionsResponse) && (
          <>
            <Heading value={3}>Document Settings</Heading>
            {formErrors.length > 0 &&
              formErrors.map((err, idx) => (
                <div key={idx} className="mb-2">
                  <Alert level="error" dismissable={false}>
                    {err}
                  </Alert>
                </div>
              ))}
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
              {renderedOptions}

              <div className="flex flex-row space-x-2 mt-6">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit(onPreview)}
                  disabled={previewMutation.isPending}
                  loading={previewMutation.isPending}
                  color={Color.WHITE}
                  className="ml-auto">
                  Preview
                </Button>
                <Button
                  type="submit"
                  color={Color.BLUE}
                  disabled={generateMutation.isPending}
                  loading={generateMutation.isPending}
                  size="sm">
                  Generate
                </Button>
              </div>
            </form>
          </>
        )}
      </Card>

      <div className="mt-8">
        <Heading value={3} className="mb-4">
          History
        </Heading>

        {isLoadingList ? (
          <div className="p-6 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            {!listResponse?.results || listResponse.results.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-white shadow rounded-lg">
                No documents found.
              </div>
            ) : (
              <ul className="space-y-4">
                {listResponse.results.map((doc) => (
                  <li key={doc.id}>
                    <Card className="p-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">{doc.name}</h4>
                          <div className="mt-1 text-sm text-gray-500">
                            Requested:{' '}
                            <DualTime
                              dateTime={new Date(doc.requested_at)}
                              memberTZ={person.time_zone}
                            />
                            <p>Requested by: {doc.requested_by.display_name}</p>
                          </div>
                          {doc.generated_at && (
                            <div className="mt-1 text-sm text-gray-500">
                              Generated:{' '}
                              <DualTime
                                dateTime={new Date(doc.generated_at)}
                                memberTZ={person.time_zone}
                              />
                            </div>
                          )}
                          {doc.description && (
                            <div className="mt-2 text-sm text-gray-700">
                              <p>{doc.description}</p>
                            </div>
                          )}
                        </div>
                        {
                          <Button
                            href={doc.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            color={Color.BLUE}
                            disabled={!doc.document_url}>
                            Download
                          </Button>
                        }
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            )}

            {listResponse && listResponse.total_pages > 1 && (
              <div className="mt-4">
                <Paginator
                  currentPage={page}
                  totalPages={listResponse.total_pages}
                  goToPage={setPage}
                  nextPage={() => setPage((p) => p + 1)}
                  prevPage={() => setPage((p) => p - 1)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PersonGeneratedDocuments;
